import { useId } from 'react';

interface AxudaLogoProps {
  /** Overall pixel size of the square mark. */
  size?: number;
  /** Show the "AXUDA" wordmark next to the mark. */
  showWordmark?: boolean;
  /** Optional tagline rendered under the wordmark. */
  showTagline?: boolean;
  /** Render the wordmark in white (for use on dark backgrounds). */
  light?: boolean;
  className?: string;
}

/**
 * AXUDA — brand logo.
 *
 * The mark is an abstract monogram "A" formed by two interlocking chevrons that
 * converge at a single apex — a visual metaphor for cooperation and a strategic
 * partnership. The emerald→teal→cyan gradient keeps AXUDA a distinct, modern
 * identity that sits alongside (and complements) the AI Agent HUB blue palette.
 */
export function AxudaLogo({
  size = 44,
  showWordmark = false,
  showTagline = false,
  light = false,
  className = '',
}: AxudaLogoProps) {
  // Unique gradient ids so multiple instances on one page don't collide.
  const uid = useId().replace(/:/g, '');
  const gradId = `axuda-grad-${uid}`;
  const shineId = `axuda-shine-${uid}`;

  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <svg
        width={size}
        height={size}
        viewBox="0 0 64 64"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        role="img"
        aria-label="AXUDA"
        className="shrink-0"
      >
        <defs>
          <linearGradient id={gradId} x1="8" y1="6" x2="56" y2="58" gradientUnits="userSpaceOnUse">
            <stop stopColor="#34d399" />
            <stop offset="0.5" stopColor="#14b8a6" />
            <stop offset="1" stopColor="#0891b2" />
          </linearGradient>
          <linearGradient id={shineId} x1="12" y1="10" x2="40" y2="40" gradientUnits="userSpaceOnUse">
            <stop stopColor="#ffffff" stopOpacity="0.45" />
            <stop offset="1" stopColor="#ffffff" stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* Rounded badge */}
        <rect x="2" y="2" width="60" height="60" rx="16" fill={`url(#${gradId})`} />
        {/* Soft top highlight for depth */}
        <rect x="2" y="2" width="60" height="60" rx="16" fill={`url(#${shineId})`} />

        {/* Left chevron of the interlocking "A" */}
        <path
          d="M32 15 L20 49 H27.5 L32 35.5 Z"
          fill="#ffffff"
          fillOpacity="0.95"
        />
        {/* Right chevron — mirrored, offset to read as "linked" halves */}
        <path
          d="M32 15 L44 49 H36.5 L32 35.5 Z"
          fill="#ffffff"
          fillOpacity="0.78"
        />
        {/* Connecting bridge — the "crossbar" that joins the two partners */}
        <rect x="26" y="38.5" width="12" height="4.5" rx="2.25" fill="#ffffff" fillOpacity="0.95" />
      </svg>

      {showWordmark && (
        <div className="flex flex-col leading-none">
          <span
            className={`font-extrabold tracking-tight ${light ? 'text-white' : 'text-slate-900'}`}
            style={{ fontSize: size * 0.5 }}
          >
            AXUDA
          </span>
          {showTagline && (
            <span
              className={`font-medium tracking-wide uppercase ${light ? 'text-teal-200/80' : 'text-teal-600'}`}
              style={{ fontSize: size * 0.18, marginTop: size * 0.06 }}
            >
              Strategic Partner
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export default AxudaLogo;
